import { Button, Chip, Container, Grid, Typography } from '@mui/material';
import { isNil, isNull } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { SOFTWARE_CHANNEL_ID, SOC_ID_MAPPING } from '../../shared/constants';
import { getPrivilegeById } from '../../shared/utils';
import { RootState } from '../../store';
import { DashboardUser } from '../../store/dashboardUser/dashboardUserTypes';
import {
  deviceReboot,
  fetchDeviceShadow,
  clearDeviceShadow,
  getDeviceList,
  updateDeviceProperties,
} from '../../store/devices/actionCreators';
import { Device, ShadowConfig } from '../../store/devices/deviceTypes';
import { getSoftwareVersions } from '../../store/software/actionCreators';
import { SoftwareVersion } from '../../store/software/softwareTypes';
import LoadingSpinner from '../Shared/LoadingSpinner';
import Modal from '../Shared/Modal';
import DeviceUpdateButton from './components/DeviceUpdateButton';
import UpdateDeviceChannel from './components/UpdateDeviceChannel';
import UnassignDevice from '../Shared/UnassignModal';
import DeleteDatabase from './components/DeleteDatabase';
import DeviceFaults from './components/DeviceFaults';
import DeviceShadowsConfig from './components/DeviceShadowsConfig';

function DeviceDetail() {
  const {
    connected,
    deviceList,
    shadowConfigs,
    softwareVersions,
    dashboardUserInfo,
  } = useSelector<
    RootState,
    {
      connected: boolean;
      deviceList: Device[] | null;
      shadowConfigs: ShadowConfig[] | null;
      softwareVersions: SoftwareVersion[] | null;
      dashboardUserInfo: DashboardUser | null;
    }
  >((state) => {
    const { mqtt, devices, software, dashboardUser } = state;
    const { connected } = mqtt;
    const { deviceList, shadowConfigs } = devices;
    const { softwareVersions } = software;
    const { dashboardUserInfo } = dashboardUser;
    return {
      connected,
      deviceList,
      shadowConfigs,
      softwareVersions,
      dashboardUserInfo,
    };
  });
  const dispatch = useDispatch();

  const { deviceSerialNumber = '' } = useParams<{
    deviceSerialNumber: string;
  }>();

  const [showRebootConfirmation, setRebootConfirmationVisibility] =
    useState(false);
  const [
    showDeleteDatabaseConfirmation,
    setDeleteDatabaseConfirmationVisibility,
  ] = useState(false);
  const [showUnassignConfirmation, setUnassignConfirmationVisibility] =
    useState(false);
  const [showFaults, setShowFaults] = useState(false);

  const device =
    deviceList &&
    deviceList.find((device) => device.serial_number === deviceSerialNumber);

  useEffect(() => {
    if (isNull(deviceList)) {
      dispatch(getDeviceList());
    }
    if (isNull(softwareVersions)) {
      dispatch(getSoftwareVersions());
    }
    if (!isNil(device) && isNull(shadowConfigs)) {
      dispatch(fetchDeviceShadow(device.id));
    }
  }, [
    connected,
    deviceList,
    shadowConfigs,
    softwareVersions,
    device,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearDeviceShadow());
    };
  }, [dispatch]);

  const isEngineer =
    dashboardUserInfo &&
    getPrivilegeById(dashboardUserInfo.privilege_id) === 'engineering';

  const rebootDevice = () => {
    if (device) {
      dispatch(deviceReboot(device.id));
    }
  };

  const updateDevice = (
    e: React.MouseEvent,
    deviceId: number,
    versionId: number,
  ) => {
    e.preventDefault();
    console.log('updating device: ' + deviceId + ' to version ' + versionId);
    dispatch(
      updateDeviceProperties(deviceId, {
        ...device,
        pinned_software_version_id: versionId,
      }),
    );
  };

  const channelMap = Object.keys(SOFTWARE_CHANNEL_ID);

  if (isNull(deviceList)) {
    return <LoadingSpinner />;
  } else
    return (
      <Container maxWidth="lg">
        <Helmet>
          <title>Ōmcare - Device Details</title>
        </Helmet>
        {(() => {
          if (!device) {
            return (
              <div className="mt-3">
                <p>Device serial number {deviceSerialNumber} not found</p>
              </div>
            );
          }
          return (
            <>
              <Grid container>
                <Grid item xs>
                  <Typography>
                    <b>Owner:</b> {device.owner_name}
                  </Typography>
                  <Typography>
                    <b>Serial number:</b> {device.serial_number}
                  </Typography>
                  <Typography>
                    <b>HW version:</b> {device.hardware_version}
                  </Typography>
                  <Typography>
                    <b>SW version:</b> {device.software_version}
                  </Typography>
                  <Typography>
                    <b>Current software channel:</b>{' '}
                    {channelMap[device.software_channel_id - 1]}
                  </Typography>
                  <Typography>
                    <b>Device soc:</b> {SOC_ID_MAPPING[device.soc_id]}
                  </Typography>
                </Grid>
                <Grid item xs style={{ textAlign: 'right' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginBottom: 10,
                    }}
                  >
                    <Typography sx={{ marginRight: 5 }}>
                      <b>Status:</b>
                    </Typography>
                    <Chip
                      label={device.status.toUpperCase()}
                      color={device.status === 'online' ? 'primary' : 'default'}
                    />
                  </div>
                  <div style={{ marginBottom: 5 }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => setRebootConfirmationVisibility(true)}
                    >
                      Reboot
                    </Button>
                  </div>
                  <DeviceUpdateButton
                    action={updateDevice}
                    softwareVersions={softwareVersions || []}
                    device={device}
                  />
                  {isEngineer ? <UpdateDeviceChannel device={device} /> : null}
                  <div style={{ marginBottom: 5 }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => setUnassignConfirmationVisibility(true)}
                      // OM-3539: Temporarily allow factory reset (device unassign) while owner is not assigned to device
                      // disabled={!device.customer_id}
                    >
                      Unassign device
                    </Button>
                  </div>
                  <div style={{ marginBottom: 5 }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() =>
                        setDeleteDatabaseConfirmationVisibility(true)
                      }
                    >
                      Delete database
                    </Button>
                  </div>
                  {isEngineer && (
                    <div style={{ marginBottom: 5 }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setShowFaults(true)}
                      >
                        See Faults
                      </Button>
                    </div>
                  )}
                </Grid>
              </Grid>

              {isEngineer && showFaults && device && (
                <DeviceFaults
                  deviceId={device.id}
                  onClose={() => setShowFaults(false)}
                />
              )}

              <div style={{ margin: '20px 0px' }}>
                <DeviceShadowsConfig
                  configs={shadowConfigs}
                  deviceId={device.id}
                />
              </div>
            </>
          );
        })()}
        {showRebootConfirmation ? (
          <Modal
            onClose={() => setRebootConfirmationVisibility(false)}
            actions={{
              primaryAction: {
                onClick: () => {
                  rebootDevice();
                  setRebootConfirmationVisibility(false);
                },
                text: 'Reboot',
              },
            }}
          >
            <span>Are you sure you want to reboot this device?</span>
          </Modal>
        ) : null}
        {showUnassignConfirmation && device ? (
          <UnassignDevice
            deviceId={device.id}
            onCancel={() => setUnassignConfirmationVisibility(false)}
            onSuccess={() => setUnassignConfirmationVisibility(false)}
          />
        ) : null}
        {showDeleteDatabaseConfirmation && device ? (
          <DeleteDatabase
            deviceId={device.id}
            ownerName={device.owner_name}
            onCancel={() => setDeleteDatabaseConfirmationVisibility(false)}
            onSuccess={() => setDeleteDatabaseConfirmationVisibility(false)}
          />
        ) : null}
      </Container>
    );
}

export default DeviceDetail;
